import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import * as IInputs from '../../../../../interfaces/merchants/products/purchases/inputs';
import { Me } from '../../../../../interfaces/users';
import { inputPurchasingPayment } from '../../../../../redux/actions/merchants/products/purchases/inputs';
import { Dispatch } from '../../../../../redux/reducers/merchants/products/purchases/inputs';
import scrollToTop from '../../../../hoc/scrollToTop';
import PurchaseSubmitButton from '../common/buttons/PurchaseSubmitButton';
import PaymentCreditCard, {
  submittable as creditCardSubmittable,
} from './PaymentCreditCard';
import PaymentMethodsSelector from './PaymentMethodsSelector';

interface ElementProps {
  schemeType: IPurchases.SchemeType;
  settings: IPurchases.SettingPayment;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
}

interface StateProps {
  me: Me;
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      schemeType: IPurchases.SchemeType,
      productId: string,
      payment: IInputs.Payment,
    ) => Promise<void>;
  };
}

interface State {
  creditCard?: IPurchases.CreditCard;
  selection?: IPurchases.PaymentSelection;
  control: Control;
}

interface Control {
  submittable?: boolean;
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

const showableInputCreditCard = (
  me: Me,
  selection?: IPurchases.PaymentSelection,
): boolean => {
  if (!me.creditCard) {
    return true;
  }
  if (!selection) {
    return false;
  }
  return selection === IPurchases.PaymentSelection.AnotherCreditCard;
};

const createSubmittable = (me: Me, state: State): boolean => {
  if (!me.creditCard) {
    return !!state.creditCard && creditCardSubmittable(state.creditCard);
  }
  if (!state.selection) {
    return false;
  }
  if (state.selection === IPurchases.PaymentSelection.UsedCreditCard) {
    return true;
  }
  return !!state.creditCard && creditCardSubmittable(state.creditCard);
};

class Payment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { control: { submittable: false } };
    this.handleSelect = this.handleSelect.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.createControl = this.createControl.bind(this);

    this.state = this.createInitialState();
  }

  private createInitialState(): State {
    const { me, inputs } = this.props;
    const _state: State = {
      creditCard: inputs.payment ? inputs.payment.creditCard : undefined,
      selection: me.creditCard
        ? IPurchases.PaymentSelection.UsedCreditCard
        : IPurchases.PaymentSelection.AnotherCreditCard,
      control: {},
    };
    return { ..._state, control: this.createControl(_state) };
  }

  private handleSelect(selection: IPurchases.PaymentSelection) {
    const newState = { ...this.state, selection };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private handleChange(creditCard: IPurchases.CreditCard) {
    const newState = { ...this.state, creditCard };
    this.setState({ ...newState, control: this.createControl(newState) });
  }

  private createControl(state: State): Control {
    const { me } = this.props;
    return { submittable: createSubmittable(me, state) };
  }

  private async handleSubmit() {
    const { schemeType, inputs, onSubmit } = this.props;
    try {
      await this.props.actions.handleSubmit(
        schemeType,
        inputs.productId!,
        this.state,
      );
      onSubmit();
    } catch (e) {
      // failed
    }
  }

  public render() {
    const {
      settings: { errors },
      postable,
      me,
      intl,
    } = this.props;
    const {
      creditCard,
      selection,
      control: { submittable },
    } = this.state;

    return (
      <>
        <PaymentMethodsSelector
          me={me}
          selection={selection}
          onSelect={this.handleSelect}
        />
        {showableInputCreditCard(me, selection) && (
          <PaymentCreditCard
            error={errors ? errors.creditCard : undefined}
            creditCard={creditCard || {}}
            onChange={this.handleChange}
          />
        )}
        <PurchaseSubmitButton
          type="button"
          onClick={this.handleSubmit}
          value={intl.formatMessage({
            id: 'purchases.payment.submitButton',
          })}
          disabled={!submittable || !postable}
          postable={postable}
        >
          {intl.formatMessage({
            id: 'purchases.payment.submitButton',
          })}
        </PurchaseSubmitButton>
      </>
    );
  }
}

const mapStateProps = ({
  users: { me },
  merchantsProductsPurchasesInputs,
}: {
  users: { me: Me };
  merchantsProductsPurchasesInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsProductsPurchasesInputs,
  me,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (
      schemeType: IPurchases.SchemeType,
      productId: string,
      payment: IInputs.Payment,
    ) => inputPurchasingPayment(schemeType, productId, payment)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(Payment)));
